import _ from "lodash";

/**
 * A getter function that returns the content of a node/box
 * @example
 * const getBoxContent = (box) => {
 *  if (!box) return null;
 *  return currentContent[box] ?? null;
 * }
 * @param box box name to resolve
 * @returns the box's content object, or null when unknown
 */
export type BoxNodeGetter = (box: string) => null | Record<string, any>;

/** A box's content payload from the content store (followups/answers/childBoxes/etc.) — backend shape, intentionally loose. */
type BoxContent = Record<string, any>;

/** Entry of a form's `childBoxes` array describing where a child box links to next. */
interface ChildBoxEntry {
  /** Name of the box this entry belongs to. */
  box: string;
  /** Single next box name (one-child case). */
  next?: any;
  /** Multiple child options, each pointing to a box (multi-child case). */
  values?: Array<{ box?: string; [key: string]: any }>;
  [key: string]: any;
}

// a util class for tree data structure
// a tree is a data structure that consists of nodes in a parent / child relationship
// tree has a walk method that traverses the tree and executes a callback on each node

class TreeNode {
  /** The box name this node represents. */
  box: string | undefined;
  /** Child nodes in option order. */
  children: TreeNode[];
  /** Back-references to parent boxes and the child index this node occupies in them. */
  parents: { box: string | undefined; index: number }[];
  /** Shared (tree-wide) set of already-linked "parent -> child[index]" paths, to avoid duplicates. */
  processedPaths: Set<string>;

  /**
   * @param content box name (string) or a content object carrying a `box` property
   * @param processedPaths the owning tree's shared processed-paths set
   */
  constructor(content: string | BoxContent | null | undefined, processedPaths: Set<string>) {
    // content is String than it is a box, otherwise it is a content with property box
    this.box = typeof content === 'string' ? content : content?.box;

    this.children = [];
    this.parents = [];

    this.processedPaths = processedPaths;
  }

  /**
   * Registers a parent back-reference (ignored when a parent with the same box already exists).
   * @param parent the parent node
   * @param index child position this node occupies within the parent
   */
  addParent(parent: TreeNode, index: number): void {
    // parent with this box name already exists, return
    if (this.parents.find((p) => p.box === parent.box)) {
      return;
    }

    this.parents.push({ box: parent.box, index });
  }

  /**
   * @param content an existing node, or a box name/content object to wrap in a new node
   * @param childIndex Where in the parent's index does this node exist?
   */
  appendChild(content: TreeNode | string | BoxContent | null | undefined, childIndex?: number): TreeNode {
    // `BoxContent` (Record<string, any>) survives both `instanceof` and assignment narrowing
    // since TreeNode is structurally assignable to it — an explicitly typed variable is the
    // only way to collapse the union.
    const node: TreeNode = content instanceof TreeNode
      ? (content as TreeNode)
      : new TreeNode(content, this.processedPaths);

    /**
     * If an earlier path lead here, it would have already been populated.
     * We also need the index, because we can still have A -> B, but A
     * has option 1, 2, 3, 4 and 5 that all leads to B.
     */
    if (!this.processedPaths.has(`${node.box}[${childIndex}] -> ${this.box}`)) {
      this.processedPaths.add(`${node.box}[${childIndex}] -> ${this.box}`);
      this.children.push(node);
    }
    if (!this.processedPaths.has(`${this.box} -> ${node.box}[${childIndex}]`)) {
      this.processedPaths.add(`${this.box} -> ${node.box}[${childIndex}]`);
      node.addParent(this, this.children.length);
    }

    return node;
  }

  getChildren(): TreeNode[] {
    return this.children;
  }

  getBoxName(): string | undefined {
    return this.box;
  }

  /**
   * Depth-first search for the node representing `box`.
   * @param node If string, it is treated as 'box', and `node` will be root
   * @param box box name to find (when `node` is a node)
   */
  findNode(node: TreeNode | string, box?: string | null): null | TreeNode {
    // node is string
    if (typeof node === 'string') {
      return this.findNode(this, node);
    }

    if (node.box === box) {
      return node;
    }

    for (let i = 0; i < node.children.length; i += 1) {
      const found = this.findNode(node.children[i]!, box);
      if (found) {
        return found;
      }
    }

    return null;
  }
}

class Tree {
  /** Root node of the tree; null until setRoot is called. */
  root: null | TreeNode;
  /** Flat registry of every node added to the tree (used for dedupe lookups). */
  nodes: TreeNode[];
  /**
   * Keeps track of which paths have been processed
   */
  processedPaths: Set<string>;

  constructor() {
    this.root = null;
    this.nodes = [];

    this.processedPaths = new Set();
  }

  /**
   * set the root node of the tree
   * @param content an existing node, or a box name/content object to wrap in a new node
   */
  setRoot(content: TreeNode | string | BoxContent): TreeNode {
    // if content is from TreeNode, set it as root
    if (content instanceof TreeNode) {
      this.root = content;
      return this.root;
    }

    // if content is string or content object, create a new TreeNode
    this.root = new TreeNode(content, this.processedPaths);
    this.nodes.push(this.root);
    return this.root;
  }

  getRoot(): TreeNode | null {
    return this.root;
  }

  /**
   * build the tree from content store, getBoxContent is getter function, root has to be set
   * @param getBoxContent getter function, or a plain box→content map (cloned and wrapped into a getter)
   */
  buildTree(getBoxContent: BoxNodeGetter | Record<string, BoxContent>): void {
    // if getBoxContent is not a function, return
    if (typeof getBoxContent !== 'function' && typeof getBoxContent !== 'object') {
      return;
    } else if (typeof getBoxContent === 'object') {
      // generate standard getBoxContent
      const contents = _.cloneDeep(getBoxContent);

      getBoxContent = (box: string) => {
        if (!box || !contents[box]) return null;
        return contents[box];
      };
    }

    // if root is not set, return
    if (!this.root) {
      return;
    }

    this.nodes = [];

    // build tree
    this.buildNode(this.root, getBoxContent);

    this.processedPaths.clear();
  }

  /**
   * this method is use for building tree from array of nodes, like in forms
   * @param node current node to expand
   * @param getBoxContent resolves a box name to its content
   * @param childBoxes the form's childBoxes array describing next/values links
   */
  buildNodeFromArray(node: TreeNode, getBoxContent: BoxNodeGetter, childBoxes: ChildBoxEntry[]): void {
    // no box property, end the recursion
    if (!node.box) {
      return;
    }

    const nodeContent = getBoxContent(node.box);
    // no node content, end the recursion
    if (!nodeContent) {
      return;
    }

    // get the child box from childBoxes array
    const childBox = childBoxes.find((box) => box.box === nodeContent.box);

    // child box has next property for one child or values property for multiple children
    if (childBox?.next) {
      const nodeExists = this.nodes.find((n) => n.box === childBox.next);
      const newNode = node.appendChild(nodeExists || getBoxContent(childBox.next) || childBox.next, 0);
      if (!nodeExists) {
        this.nodes.push(newNode);
      }
      this.buildNodeFromArray(newNode, getBoxContent, childBoxes);
    } else if (childBox?.values && Array.isArray(childBox.values)) {

      /**
       * childBox.values is not guaranteed to be in the right order.
       * We can also not override the correction (childBox.values = fixed)
       * because it gets detected as a change when compared to live.
       */
      const childBoxValues = this.fixFormChildValuesOrder(childBox.values, nodeContent);

      /**
       * For YesNo and MultipleChoice, we want to add a pseudo last option, which is cancel, that exists only in the frontend.
       * @see https://github.com/knowhereto/moin-hub/issues/1799
       */
      if (['BotFormYesNo', 'BotFormMultipleChoice'].includes(nodeContent.template)) {
        // Extra check to ensure we don't add multiple
        if (childBoxValues.at(-1)?.box !== 'cancel') {
          childBoxValues.push({
            box: 'cancel',
            value: 'cancel'
          });
        }
      }

      let i = 0;
      for (const value of childBoxValues) {
        if (!value.box) continue;

        const nodeExists = this.nodes.find((n) => n.box === value.box);
        const newNode = node.appendChild(nodeExists || getBoxContent(value.box) || value.box, i);
        if (!nodeExists) {
          this.nodes.push(newNode);
        }

        i++;
        this.buildNodeFromArray(newNode, getBoxContent, childBoxes);
      }
    }

    // BotFormEmail, BotFormValidableInput, BotFormFile:
    if (['BotFormEmail', 'BotFormValidableInput', 'BotFormFile'].includes(nodeContent.template)) {
      // add as second child a end node.
      const endNode = node.appendChild('end');
      this.nodes.push(endNode);
      // add as third child a cancel node.
      const cancelNode = node.appendChild('cancel');
      this.nodes.push(cancelNode);
    }
  }

  // build node and its children
  buildNode(node: TreeNode, getBoxContent: BoxNodeGetter): void {
    const nodeContent = getBoxContent(node.box as string);

    if (!nodeContent) {
      return;
    }

    // if node value has followup property, build its children
    if (nodeContent.followup && nodeContent.followup.box) {
      const nodeExists = this.nodes.find((n) => n.box === nodeContent.followup.box);
      const newNode = node.appendChild(nodeExists || getBoxContent(nodeContent.followup.box), 0);

      if (!nodeExists) {
        this.nodes.push(newNode);
        this.buildNode(newNode, getBoxContent);
      }
    } else if (nodeContent.followup && !nodeContent.followup.box) {
      node.appendChild('completion');
    } else if (nodeContent.answers && Array.isArray(nodeContent.answers)) {
      let i = 0;
      for (const answer of nodeContent.answers) {
        // copy answer to get two different objects
        const cloneOfAnswer = _.cloneDeep(answer);
        if (cloneOfAnswer.template === 'copy') {
          const copyFrom = nodeContent.answers.find((a: any, n: number) => {
            if (a.box && n !== i) {
              return true;
            }

            return false;
          });

          if (copyFrom) {
            cloneOfAnswer.box = copyFrom.box;
          }
        }

        if (!cloneOfAnswer.box) {
          node.appendChild('completion');
          continue;
        }

        const nodeExists = this.nodes.find((n) => n.box === cloneOfAnswer.box);
        const newNode = node.appendChild(nodeExists || getBoxContent(cloneOfAnswer.box), i);

        if (!nodeExists) {
          this.nodes.push(newNode);
          this.buildNode(newNode, getBoxContent);
        }

        i++;
      }
    } else {
      // handling forms
      if (nodeContent.childBoxes && Array.isArray(nodeContent.childBoxes)) {
        // get start child box
        const startChildBox = nodeContent.childBoxes.find((box: any) => box.box === nodeContent.start.next);

        if (nodeContent.childBoxes.length === 0 && nodeContent.completion || !startChildBox) {
          const completionNode = node.appendChild('completion');
          this.nodes.push(completionNode);
        } else {
          const startChildNodeContent = getBoxContent(startChildBox.box);

          // cause the botforms has the knowledge of all child nodes but not the content
          if (!startChildNodeContent) {
            return;
          }

          const nodeExists = this.nodes.find((n) => n.box === startChildNodeContent.box);
          const newNode = node.appendChild(nodeExists || getBoxContent(startChildNodeContent.box), this.nodes.indexOf(nodeExists as TreeNode));
          if (!nodeExists) {
            this.nodes.push(newNode);
          }

          this.buildNodeFromArray(newNode, getBoxContent, nodeContent.childBoxes);
        }
      }
    }

    // SimpleInformation: no branch node
    if (nodeContent.template === 'SimpleInformation') {
      // just add as second child a completion node.
      const completionNode = node.appendChild('completion');
      this.nodes.push(completionNode);
    }
  }

  /**
   * insert a node into the tree
   * @param box box name (or content) to insert
   * @param parent parent node, or a box name to locate it by
   * @returns the new child node, or null when the parent is missing or insertion would create a cycle
   */
  insert(box: string, parent: TreeNode | string): TreeNode | null {
    // if parent is instance of TreeNode set it as parent otherwise find it
    const parentNode = parent instanceof TreeNode ? parent : this.findNode(parent);

    if (parentNode) {
      // check if the box is present at path between parent and root
      const path = this.getPath(parentNode.box);
      if (path.includes(box)) {
        // attention circular, return null
        return null;
      }

      return parentNode.appendChild(box);
    }

    // if parent is not found, return null
    return null;
  }

  /**
   * @param node If string, it is treated as 'box', and `node` will be root
   * @param box box name to find (when `node` is a node)
   * @returns NOTE (pre-existing quirk): only the string form returns the found node;
   *   the node form recurses without returning (yields undefined)
   */
  findNode(node: TreeNode | string, box?: string | null): TreeNode | null | undefined | void {
    // node is string
    if (typeof node === 'string') {
      // Throws if root is unset — same as the original JS
      return this.root!.findNode(node);
    }

    if (!node) {
      return null;
    }

    node.findNode(node, box);
  }

  /**
   * Path of box names from root to `box`, honoring the user's selected options at branch nodes.
   * @param box target box name (nullish walks to the default leaf)
   * @param selectedNodes map of branch box → selected option index (`{ [box]: { question: index } }`)
   */
  getCurrentPath(box: string | null | undefined, selectedNodes: Record<string, { question: number }> = {}): string[] {
    // walk the tree and find with the box and walk along the selectedNodes { [box]: { [property]: index } }
    const path: string[] = [];
    this.getCurrentPathNode(this.root as TreeNode, box, selectedNodes, path);
    return path.reverse();
  }

  /**
   * Recursive worker for {@link getCurrentPath}; appends visited boxes to `path` bottom-up.
   * @param node subtree to search
   * @param box target box name
   * @param selectedNodes map of branch box → selected option index
   * @param path accumulator (filled in reverse order)
   * @returns whether the target was found in this subtree
   */
  getCurrentPathNode(node: TreeNode, box: string | null | undefined, selectedNodes: Record<string, { question: number }>, path: (string | undefined)[]): boolean {
    if (node?.box === box) {
      path.push(node.box);
      return true;
    }

    if (node.children.length === 0 && box) {
      return false;
    } else if (node.children.length === 0) {
      path.push(node.box);
      return true;
    }

    if (node.children.length === 1) {
      const found = this.getCurrentPathNode(node.children[0]!, box, selectedNodes, path);
      if (found) {
        path.push(node.box);
        return true;
      }
    }

    if (node.children.length > 1) {
      if (selectedNodes[node.box as string]) {
        let selectedOption = selectedNodes[node.box as string]!.question || 0;

        if (selectedOption >= node.children.length) {
          selectedOption = node.children.length - 1;
        }

        const found = this.getCurrentPathNode(node.children[selectedOption]!, box, selectedNodes, path);
        if (found) {
          path.push(node.box);
          return true;
        }
      } else {
        const found = this.getCurrentPathNode(node.children[0]!, box, selectedNodes, path);
        if (found) {
          path.push(node.box);
          return true;
        }
      }
    }

    return false;
  }

  /** Path of box names from `box` up to the root (unordered walk accumulator). */
  getPath(box: string | undefined): (string | undefined)[] {
    const path: (string | undefined)[] = [];
    this.getPathNode(this.root as TreeNode, box, path);
    return path;
  }

  /** Recursive worker for {@link getPath}. Returns whether `box` was found in this subtree. */
  getPathNode(node: TreeNode, box: string | undefined, path: (string | undefined)[]): boolean {
    if (node.box === box) {
      path.push(node.box);
      return true;
    }

    for (let i = 0; i < node.children.length; i += 1) {
      const found = this.getPathNode(node.children[i]!, box, path);
      if (found) {
        path.push(node.box);
        return true;
      }
    }

    return false;
  }

  /** Removes the first child matching `box` (NOTE: pre-existing — only descends the first child branch). */
  remove(box: string): TreeNode | undefined {
    return this.removeNode(this.root as TreeNode, box);
  }

  /** Recursive worker for {@link remove}; returns the parent the child was removed from. */
  removeNode(node: TreeNode, box: string): TreeNode | undefined {
    if (node.children.length === 0) {
      return;
    }

    for (let i = 0; i < node.children.length; i += 1) {
      if (node.children[i]!.box === box) {
        node.children.splice(i, 1);
        return node;
      }
      return this.removeNode(node.children[i]!, box);
    }
  }

  /** Recursive worker for {@link getAllNodesWithDepth}. */
  getAllNodesWithDepthNode(node: TreeNode, result: { box: string | undefined; depth: number }[], depth: number): void {
    result.push({ box: node.box, depth });
    for (let i = 0; i < node.children.length; i += 1) {
      this.getAllNodesWithDepthNode(node.children[i]!, result, depth + 1);
    }
  }

  /** Every node's box name paired with its depth, sorted shallow → deep. */
  getAllNodesWithDepth(): { box: string | undefined; depth: number }[] {
    const result: { box: string | undefined; depth: number }[] = [];
    this.getAllNodesWithDepthNode(this.root as TreeNode, result, 0);
    // sort results by depth from low to high
    result.sort((a, b) => a.depth - b.depth);

    return result;
  }

  /**
   * Pre-order dump of the tree for debugging.
   * @param printFunc formats one node given its parent (default: `"<box> ->"`)
   */
  print(printFunc: (parentNode: TreeNode | null, node: TreeNode) => any = (parentNode, node) => `${node.box} ->`): any[] {
    return this.printNode(null, this.root as TreeNode, printFunc);
  }

  /** Recursive worker for {@link print}. */
  printNode(parentNode: TreeNode | null, node: TreeNode, printFunc: (parentNode: TreeNode | null, node: TreeNode) => any): any[] {
    let nodes: any[] = [];
    if (!node) return nodes;

    nodes.push(printFunc(parentNode, node));

    for (let i = 0; i < node.children.length; i += 1) {
      nodes = nodes.concat(this.printNode(node, node.children[i]!, printFunc));
    }
    return nodes;
  }

  /**
   * Reorders a form childBox's `values` to match the order the quick-replies/answers
   * are actually shown in, using the node's content as source of truth.
   * @param valuesArray the childBox.values candidates to reorder
   * @param node content of the parent box (provides `answers` or `question.actions[].content.quick` ordering)
   * @returns a NEW reordered array (input untouched), or the input when no ordering info exists
   */
  fixFormChildValuesOrder(valuesArray: Array<{ box?: string; value?: any; [key: string]: any }>, node: BoxContent): Array<{ box?: string; value?: any; [key: string]: any }> {
    if (node.loading) return valuesArray; // No need to process if not loaded
    /** Ordering source of truth: entries carrying the `intent` each option points to. */
    let arrayOfIntents: Array<{ intent: string; [key: string]: any }> | null = null;

    if (node.template === 'BotFormConditionalSwitch') {
      // Special case: BotFormConditionalSwitch stores it directly in root
      arrayOfIntents = node.answers;
    } else {
      // Most cases
      const found = node?.question?.actions?.findLast((box: any) => !!box?.content?.quick);
      if (found) arrayOfIntents = found.content.quick;
    }

    // We can't do anything without this
    if (!arrayOfIntents) return valuesArray;

    // Clone the array
    const candidates = Array.from(valuesArray);
    /**
     * Due to some complexity with an intent being referenced many times, and at different index positions,
     * we must use a queue to pull the desired item from, and then make sure we never re-visit it.
     *
     * arrayOfIntents is the source of truth for order, and valuesArray is our candidates
     * E.g. valuesArray = [A, B, C, A, D]
     *   arrayOfIntents = [A, B, A, C, D]
     *
     * If we just use sort, valuesArray's A will always end up in the first position.
     */

    const newArray: Array<{ box?: string; value?: any; [key: string]: any }> = [];

    // Sort it based on arrayOfIntents
    for (let i=0; i < arrayOfIntents.length; i++) {
      const intent = arrayOfIntents[i]!.intent;
      // the second part of the check (v.box === intent) is for the completion path.
      const index = candidates.findIndex((v) => v.value === intent || v.box === intent);
      if (index === -1) continue; // Nothing we can do, but should never really happen

      // Get the item from the queue
      newArray.push( candidates.splice(index, 1)[0]! );
    }

    return newArray;
  }
}

// export Tree and TreeNode
export { Tree, TreeNode };
